import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getContentItems,
  getContentItem,
  getContentLibrarySummary,
  deleteContentItem,
  generateContent,
  researchTopic,
  repurposeContent,
  getTopicClusters,
  createTopicCluster,
  getGeneratorTypes,
  getDerivedItems,
  patchContentItem,
  approveContentItem,
  exportToSanity,
  createImpactArticle,
  getContentVerification,
  clearVerificationBlock,
  getSanityTypes,
  listSanityDocumentTypes,
  duplicateSanityStructure,
  generateFromStructure,
  getContentVersions,
  restoreContentVersion,
  bulkGenerateFromNotion,
  getSanitySchemaProposal,
  exportToSanityWithSchemaCheck,
  getRewriteRouteTable,
  getRewriteNotionSources,
  previewRewrite,
  getRewriteProject,
  saveProposedEnvelope,
  pushRewrite,
  listModifications,
  pollRewriteProjectNow,
  reExtractModification,
  deleteModification,
  importWebsiteUrls,
  generateWebsiteRefresh,
  getWebsiteReview,
  saveWebsiteReviewSnapshot,
  finishLinkedInReview,
  importVoiceSamples,
  getVoiceSampleCount,
  type VoicePersona,
  pushWebsiteReviewDraft,
  publishWebsiteReviewDraft,
  reopenWebsiteReview,
  recalculateWebsiteReviewScore,
  refreshWebsiteReviewSerpEntities,
} from '../api/content.js';
import type { ContentItemFilters, GenerateInput, RepurposeInput, BulkGenerateInput, GenerateFromStructureInput } from '../api/content.js';
import type { ContentLanguage, ContentType, IcpSegment } from '@sequesto-os/shared';

export function useContentItems(filters: ContentItemFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['content', 'items', filters],
    queryFn: () => getContentItems(filters),
    enabled: options?.enabled ?? true,
    placeholderData: (prev) => prev,
    staleTime: 2 * 60_000,
  });
}

export function useContentItem(id: string | null) {
  return useQuery({
    queryKey: ['content', 'item', id],
    queryFn: () => getContentItem(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      // Poll while generating
      const data = query.state.data;
      if (data && (data.metadata as Record<string, unknown>)?.generating) {
        return 3000;
      }
      return false;
    },
  });
}

export function useDeleteContentItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteContentItem(id),
    onSuccess: (_result, id) => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'library-summary'] });
      qc.removeQueries({ queryKey: ['content', 'item', id] });
      qc.removeQueries({ queryKey: ['content', 'versions', id] });
      qc.removeQueries({ queryKey: ['content', 'website-review', id] });
      qc.removeQueries({ queryKey: ['content', 'rewrite', 'project', id] });
      qc.removeQueries({ queryKey: ['content', 'modifications', id] });
    },
  });
}

export function useDerivedItems(parentId: string | null) {
  return useQuery({
    queryKey: ['content', 'derived', parentId],
    queryFn: () => getDerivedItems(parentId!),
    enabled: !!parentId,
  });
}

export function useTopicClusters() {
  return useQuery({
    queryKey: ['content', 'topic-clusters'],
    queryFn: getTopicClusters,
    staleTime: 5 * 60_000,
  });
}

export function useGeneratorTypes() {
  return useQuery({
    queryKey: ['content', 'generator-types'],
    queryFn: getGeneratorTypes,
    staleTime: 30 * 60_000,
  });
}

export function useContentGenerate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateInput) => generateContent(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
    },
  });
}

export function useContentVerification(id: string | null) {
  return useQuery({
    queryKey: ['content', 'verification', id],
    queryFn: () => getContentVerification(id!),
    enabled: !!id,
  });
}

export function useClearVerificationBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clearVerificationBlock(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['content', 'verification', id] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
    },
  });
}

export function useCreateImpactArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof createImpactArticle>[0]) => createImpactArticle(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
    },
  });
}

export function useResearch() {
  return useMutation({
    mutationFn: (input: {
      topic: string;
      language?: ContentLanguage;
      contentType?: ContentType;
      icpSegments?: IcpSegment[];
      skipCache?: boolean;
    }) => researchTopic(input),
  });
}

export function useRepurpose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RepurposeInput) => repurposeContent(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
    },
  });
}

export function useCreateTopicCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; slug: string; pillarKeyword?: string; description?: string; language?: ContentLanguage }) =>
      createTopicCluster(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'topic-clusters'] });
    },
  });
}

export function usePatchContentItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof patchContentItem>[1] }) =>
      patchContentItem(id, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'library-summary'] });
      qc.invalidateQueries({ queryKey: ['content', 'item'] });
      qc.invalidateQueries({ queryKey: ['content', 'rewrite', 'project'] });
    },
  });
}

export function useApproveContentItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveContentItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'library-summary'] });
      qc.invalidateQueries({ queryKey: ['content', 'item'] });
      qc.invalidateQueries({ queryKey: ['content', 'rewrite', 'project'] });
    },
  });
}

export function useSanityTypes() {
  return useQuery({
    queryKey: ['content', 'sanity-types'],
    queryFn: getSanityTypes,
    staleTime: 10 * 60_000,
    enabled: false, // Only fetch on demand
  });
}

export function useSanityDocumentTypes(enabled = true) {
  return useQuery({
    queryKey: ['content', 'sanity', 'document-types'],
    queryFn: listSanityDocumentTypes,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useDuplicateSanityStructure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sanityType: string) => duplicateSanityStructure(sanityType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
    },
  });
}

export function useGenerateFromStructure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contentItemId, input }: { contentItemId: string; input: GenerateFromStructureInput }) =>
      generateFromStructure(contentItemId, input),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'item', variables.contentItemId] });
    },
  });
}

export function useExportToSanity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, documentType }: { id: string; documentType?: string }) =>
      exportToSanity(id, documentType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'item'] });
    },
  });
}

/**
 * Smart export that returns a schema proposal when the target Sanity doc type
 * doesn't exist yet, instead of failing. The caller renders the proposal in UI
 * and the user pastes it into their Studio repo.
 */
export function useExportToSanityWithSchemaCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, documentType }: { id: string; documentType: string }) =>
      exportToSanityWithSchemaCheck(id, documentType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'item'] });
    },
  });
}

export function useContentVersions(id: string | null) {
  return useQuery({
    queryKey: ['content', 'versions', id],
    queryFn: () => getContentVersions(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useRestoreContentVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contentItemId, versionId }: { contentItemId: string; versionId: string }) =>
      restoreContentVersion(contentItemId, versionId),
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['content', 'item', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'versions', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'website-review', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'modifications', vars.contentItemId] });
    },
  });
}

export function useBulkGenerate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkGenerateInput) => bulkGenerateFromNotion(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
    },
  });
}

export function useImportWebsiteUrls() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof importWebsiteUrls>[0]) => importWebsiteUrls(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'item'] });
    },
  });
}

export function useGenerateWebsiteRefresh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input?: Parameters<typeof generateWebsiteRefresh>[1] }) =>
      generateWebsiteRefresh(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'item'] });
    },
  });
}

export function useWebsiteReview(contentItemId: string | null) {
  return useQuery({
    queryKey: ['content', 'website-review', contentItemId],
    queryFn: () => getWebsiteReview(contentItemId!),
    enabled: !!contentItemId,
    staleTime: 30_000,
  });
}

export function useSaveWebsiteReviewSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof saveWebsiteReviewSnapshot>[0]) => saveWebsiteReviewSnapshot(input),
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['content', 'item', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'versions', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'website-review', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'modifications', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'feedback-rules'] });
    },
  });
}

export function useVoiceSampleCount(person: VoicePersona | null) {
  return useQuery({
    queryKey: ['content', 'voice-samples', 'count', person],
    queryFn: () => getVoiceSampleCount(person!),
    enabled: !!person,
    staleTime: 30_000,
  });
}

export function useImportVoiceSamples() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof importVoiceSamples>[0]) => importVoiceSamples(input),
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'library-summary'] });
      qc.invalidateQueries({ queryKey: ['content', 'voice-samples', 'count', vars.person] });
    },
  });
}

export function useContentLibrarySummary() {
  return useQuery({
    queryKey: ['content', 'library-summary'],
    queryFn: () => getContentLibrarySummary(),
    staleTime: 30_000,
  });
}

export function useFinishLinkedInReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof finishLinkedInReview>[0]) => finishLinkedInReview(input),
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['content', 'item', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'versions', vars.contentItemId] });
    },
  });
}

export function usePushWebsiteReviewDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contentItemId, confirmOverwrite }: { contentItemId: string; confirmOverwrite?: boolean }) =>
      pushWebsiteReviewDraft(contentItemId, { confirmOverwrite }),
    onSuccess: (result, { contentItemId }) => {
      // 409 "requires confirmation" responses flow through here too — only
      // invalidate caches when the push actually went through.
      if ('requiresConfirmation' in result) return;
      qc.invalidateQueries({ queryKey: ['content', 'item', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'website-review', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'modifications', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'feedback-rules'] });
    },
  });
}

export function usePublishWebsiteReviewDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contentItemId: string) => publishWebsiteReviewDraft(contentItemId),
    onSuccess: (_result, contentItemId) => {
      qc.invalidateQueries({ queryKey: ['content', 'item', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'versions', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'website-review', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'modifications', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'feedback-rules'] });
    },
  });
}

export function useReopenWebsiteReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contentItemId: string) => reopenWebsiteReview(contentItemId),
    onSuccess: (_result, contentItemId) => {
      qc.invalidateQueries({ queryKey: ['content', 'item', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'versions', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'website-review', contentItemId] });
    },
  });
}

export function useRecalculateWebsiteReviewScore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contentItemId: string) => recalculateWebsiteReviewScore(contentItemId),
    onSuccess: (_result, contentItemId) => {
      qc.invalidateQueries({ queryKey: ['content', 'item', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'website-review', contentItemId] });
    },
  });
}

export function useRefreshSerpEntities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contentItemId: string) => refreshWebsiteReviewSerpEntities(contentItemId),
    onSuccess: (_result, contentItemId) => {
      qc.invalidateQueries({ queryKey: ['content', 'item', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'website-review', contentItemId] });
    },
  });
}

export function useSanitySchemaProposal(pageType: string | null) {
  return useQuery({
    queryKey: ['content', 'sanity-schema-proposal', pageType],
    queryFn: () => getSanitySchemaProposal(pageType!),
    enabled: false,
    staleTime: 5 * 60_000,
  });
}

/* ─── Rewrite from URL ─── */

export function useRewriteRouteTable() {
  return useQuery({
    queryKey: ['content', 'rewrite', 'route-table'],
    queryFn: getRewriteRouteTable,
    staleTime: 60 * 60_000,
  });
}

export function useRewriteNotionSources() {
  return useQuery({
    queryKey: ['content', 'rewrite', 'notion-sources'],
    queryFn: getRewriteNotionSources,
    staleTime: 60 * 60_000,
  });
}

export function usePreviewRewrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof previewRewrite>[0]) => previewRewrite(input),
    onSuccess: (result) => {
      if (result.status !== 'questions') {
        qc.invalidateQueries({ queryKey: ['content', 'items'] });
      }
    },
  });
}

export function useRewriteProject(contentItemId: string | null) {
  return useQuery({
    queryKey: ['content', 'rewrite', 'project', contentItemId],
    queryFn: () => getRewriteProject(contentItemId!),
    enabled: !!contentItemId,
  });
}

export function useSaveProposedEnvelope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof saveProposedEnvelope>[0]) => saveProposedEnvelope(input),
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['content', 'item', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'versions', vars.contentItemId] });
    },
  });
}

export function usePushRewrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof pushRewrite>[0]) => pushRewrite(input),
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['content', 'item', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'modifications', vars.contentItemId] });
    },
  });
}

export function useRewriteModifications(contentItemId: string | null) {
  return useQuery({
    queryKey: ['content', 'modifications', contentItemId],
    queryFn: () => listModifications(contentItemId!),
    enabled: !!contentItemId,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.modifications.some((mod) => mod.extractionStatus === 'pending') ? 3_000 : false;
    },
  });
}

export function usePollRewriteNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contentItemId: string) => pollRewriteProjectNow(contentItemId),
    onSuccess: (_result, contentItemId) => {
      qc.invalidateQueries({ queryKey: ['content', 'modifications', contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'feedback-rules'] });
    },
  });
}

export function useReExtractModification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { modificationId: string; contentItemId: string }) =>
      reExtractModification(input.modificationId),
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['content', 'modifications', vars.contentItemId] });
      qc.invalidateQueries({ queryKey: ['content', 'feedback-rules'] });
    },
  });
}

export function useDeleteModification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { modificationId: string; contentItemId: string }) =>
      deleteModification(input.modificationId),
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['content', 'modifications', vars.contentItemId] });
    },
  });
}
